import React, { useState, useEffect, Fragment, ReactNode } from 'react';
import { Table, Form, FormInstance, Input, Popover, Button } from 'antd';
const { Column } = Table;
import { DeleteOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';

import AntAutoCompleteV2, {
    AntAutoCompleteProps,
    OptionTypeBase,
    OptionTypeBaseObj,
} from './AntAutoComplete';
import {
    apiDrugToMedicationEntity,
    Inventory,
    InventoryItem,
    medicinesInventoryStatusOptions,
    PRESCRIBE_V2_AS_INPUT,
} from '../PrescribeAutoSuggestV2';

import { fetchMatchingDrugsLabTests } from '../../../../../api';
import {
    LabTestsEntityOrProceduresEntity,
    MedDose,
    MedDoseInfo,
    MedicationsEntity,
    MedRoute,
    QuantityWithUnit,
    Tool,
} from '../../../../../store/types/prescription';
import { GMedFrequency, GMedication, GMedSOA, GMedTiming } from '../../../../../store/db';
import { track } from '../../../../../tracking';
import { getG, getGArray } from '../../../../../store/idbHandler';
import { Drug, S_TYPE } from '../../../../../api/fetchMatchingDrugsLabTests';
import { generateElementId, getUnit, randomString, uniqueBy } from '../../../../../store/utils';
import {
    LOCAL_ID_PREFIX,
    MED_TABLE_COL_DOSE_KEY,
    MED_TABLE_COL_DURATION_KEY,
    MED_TABLE_COL_FREQUENCY_KEY,
    MED_TABLE_COL_INSTRUCTION_KEY,
    MED_TABLE_COL_QUANTITY_KEY,
    MED_TABLE_COL_START_FROM_KEY,
    MED_TABLE_COL_TIMING_KEY,
} from '../../../../../constants';
import PROPERTIES from '../properties';
import {
    LAB_TESTS_FOLLOWUP_DATE_TYPE,
    LAB_TESTS_FOLLOWUP_TIME_TYPE,
    generateDurationOptions,
    generateStartFromOptions,
    generateToDayOptions,
} from '../../../../common/optionGeneratorUtils';
import { DragHandle, SortableContainer, SortableItem } from './TableWithDragableRows';
import { generateDoseOptions } from '../ExpandedMedCard';
import MX_DOSAGE_FORM_UNIT, {
    FORM_TO_DF_MAPPING,
    ROUTES,
    SITE_OF_APPLICATION,
} from '../mxProperties';
import { getDoseStr } from '../MedCard';
import { PAD_V2_CONTAINER } from '../PadContainer';
import CopyToAll from './CopyToAll';
import {
    API_SEARCH,
    AS_INLINE_EMPTY,
    AS_INLINE_SEARCH,
    CUSTOM,
    GENIE,
    LAB_TESTS_KIND,
    MEDICINES_KIND,
} from '../../../trackSource';
import { WarningIcon, capsulesIcon, labsIcon } from '../../../../common/Icons';
import { AntTooltip } from '../../../../common/AntTooltip';
import { GeniePadElementsSettingItem } from '../../../../../api/pushGenie';
import {
    columnHeaderContent,
    handleTableDragWidth,
    headerStyle,
} from '../../../../common/TableUtils';
import { SortStart } from 'react-sortable-hoc';
import RenameMedName from './RenameMedName';
import { onFormSearch } from './onFormSearch';
import AddDrugStrip from './AddDrugStrip';
import { batch, connect } from 'react-redux';
import AddDrugDialogWrapper from '../AddDrugDialogWrapper';
import { FlatMedObj, getFlatArray, getUncodedDrugs } from './getFlatArray';
import { onFrequencySearch } from './onFrequencySearch';
import { onTimingSearch } from './onTimingSearch';
import LabTestsDateSelector, { LAB_TESTS_FOLLOWUP_TYPE } from './LabTestsDateSelector';
import LabTestsIncludedInPackagePanel from './LabTestsIncludedInPackagePanel';
import {
    isOnboardedForLab,
    isOnboardedForPharma,
    isHXNGVendorPackageOrDoctorPackage,
} from '../../../../../pages/utils';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import { GlobalState } from '../../../../../store/reducers';
import { getDrugsSubstitute, getRxClinicId } from '../../../../../store/selectors/prescriptionPad';
import { TestingType } from '../../finishScreen/BookLabTestsUtils';
import { SubstitutesResponse } from '../../../../../api/fetchDrugsSubstitute';
import { showDialog } from '../../../../../store/actions/prescriptionPad';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../../store/actions';
import { DB_TABLES } from '../../../../../store/dbTables';
import { getMyDbId } from '../../../../../api/mactiveid';
import CalculatorRegularIcon from '../../../../../icons/regular/calculator-regular';
import { CalculatorRightPopUp } from './CalculatorRightPopUp';
import { CALCULATORS_NAMES } from '../calculatorsTypes';
import { FREQUENCY_TO_TEXT_VALUE } from './medUtils';
import { getGenieSetting } from '../../../../../store/selectors/session';

const MED_TABLE_CONTAINER = 'MED_TABLE_CONTAINER';

interface EditingForm {
    [key: string]: string;
}

const EditableContext = React.createContext<FormInstance<EditingForm> | null>(null);

interface P<T extends OptionTypeBase> extends AntAutoCompleteProps<T> {
    label: string;
    fieldName: string;
    withoutError?: boolean;
    withoutDiv?: boolean;
    containerId?: string;
}

export const ALL_FORM_UNITS_FOR_MX = uniqueBy(
    'id',
    Object.values(MX_DOSAGE_FORM_UNIT)
        .reduce((all, df) => {
            all.push(...df.unit_list);
            return all;
        }, [] as { id: string; name: string }[])
        .sort((a, b) => b.name.localeCompare(a.name)),
);

export const CustomEntityTooltip: React.FC<{ id: string; kind?: string }> = ({ id, kind }) =>
    id.startsWith(LOCAL_ID_PREFIX) ? (
        <AntTooltip
            placement="bottom"
            content={`Custom ${kind || 'drug'} added by you`}
            getPopupContainer={() => document.getElementById(id) || document.body}
        >
            <div className="flex items-center">{WarningIcon()}</div>
        </AntTooltip>
    ) : null;

// TODO, check if V2 can be reused here
/**
 * @deprecated use WditableCellV2
 */
export const EditableCell = <OT extends OptionTypeBase>(props: P<OT>): JSX.Element => {
    const [editing, setEditing] = useState(
        props.withoutError ? false : props.selectedValue === undefined,
    );

    const [form] = Form.useForm();

    useEffect(() => {
        if (props.withoutError ? false : props.selectedValue === undefined) {
            const t = props.selectedValue;
            const tt = {
                [props.fieldName]: t ? props.getLabelToCompare(t) : undefined,
            };
            try {
                form?.setFieldsValue(tt);
                form?.validateFields();
            } catch (errInfo) {}
        }
    }, []);

    const toggleEdit = () => {
        setEditing(!editing);

        form &&
            form.setFieldsValue({
                [props.fieldName]: props.selectedValue
                    ? props.getLabelToCompare(props.selectedValue)
                    : undefined,
            });
    };

    const onSelect = async (t?: OT) => {
        const tt = {
            [props.fieldName]: t ? props.getLabelToCompare(t) : undefined,
        };
        try {
            form?.setFieldsValue(tt);
            await form?.validateFields();
            // Only toggle if value is selected
            // In case value is cleared, let it be editable
            t && toggleEdit();
        } catch (errInfo) {
            console.error('Save failed:', errInfo);
        }

        props.onSelect(t);
    };

    const onBlur = async () => {
        if (props.withoutError) {
            setEditing(false);
            return;
        }
        try {
            await form?.validateFields();
            toggleEdit();
        } catch (errInfo) {
            console.error('Save failed:', errInfo);
        }
    };

    let childNode = null;

    if (editing || props.withoutDiv) {
        const node = (
            <AntAutoCompleteV2
                containerIdToAttachDropdown={props.containerId}
                autoFocus={
                    (props.withoutError ? false : props.selectedValue === undefined) ? false : true
                }
                {...props}
                onSelect={onSelect}
                // Todo, this is not clean solution
                // there should be seperation of concern onSelect vs onBlur
                onBlur={onBlur}
            />
        );

        childNode = props.withoutError ? (
            node
        ) : (
            <Form form={form} component={false}>
                {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore */}
                <EditableContext.Provider value={form}>
                    <Form.Item
                        style={{
                            marginBottom: 0,
                            width: '100%',
                        }}
                        name={props.fieldName}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <span className="font-400 text-10">
                                        {props.label} is required.
                                    </span>
                                ),
                            },
                        ]}
                    >
                        {node}
                    </Form.Item>
                </EditableContext.Provider>
            </Form>
        );
    } else {
        childNode = (
            <div
                ref={(ref) => ref && props.setRef && props.setRef(ref)}
                className="editable-cell-value-wrap cursor-pointer w-full"
                onClick={toggleEdit}
                tabIndex={0}
                onFocus={toggleEdit}
            >
                {(props.selectedValue && props.getLabelToCompare(props.selectedValue)) || (
                    <p className="text-darwin-neutral-400 truncate">{`${
                        props.placeholder || props.label
                    }`}</p>
                )}
            </div>
        );
    }

    return childNode;
};

type OnBlurTypes = HTMLInputElement | HTMLTextAreaElement;
type EditableCellV2Props = {
    children: JSX.Element;
    onBlur?: (e: React.FocusEvent<OnBlurTypes>) => void;
    defaultValue?: string;
    label: string;
    setRef?: (ref: EkaFocosableElement) => void;
    triggerEvents?: 'onBlur';
    defaultEditing?: boolean;
    withError?: boolean;
    fieldName?: string;
    tabIndex?: number;
};

export const EditableCellV2 = (editableCellprops: EditableCellV2Props): JSX.Element => {
    const [editing, setEditing] = useState(editableCellprops.defaultEditing || false);

    const [form] = Form.useForm();
    const [fieldName] = useState(editableCellprops.fieldName || randomString(4));

    const selectedValue = editableCellprops.defaultValue;

    useEffect(() => {
        if (editableCellprops.withError) {
            const tt = {
                [fieldName]: selectedValue || undefined,
            };
            try {
                form?.setFieldsValue(tt);
                form?.validateFields();
            } catch (errInfo) {}
        }
    }, [selectedValue]);

    const toggleEdit = () => {
        setEditing(!editing);

        form &&
            form.setFieldsValue({
                [fieldName]: selectedValue ? selectedValue : undefined,
            });
    };

    const onBlur = async (e: React.FocusEvent<OnBlurTypes>) => {
        try {
            if (editableCellprops.withError) {
                const tt = {
                    [fieldName]: e.target.value,
                };
                form?.setFieldsValue(tt);
                await form?.validateFields();
            }
            toggleEdit();
        } catch (errInfo) {
            console.error('Save failed:', errInfo);
        }

        editableCellprops.onBlur && editableCellprops.onBlur(e);
    };

    const { triggerEvents } = editableCellprops;

    if (editing) {
        const { props } = editableCellprops.children;
        const clonedProps = { ...props };
        clonedProps['setRef'] = editableCellprops.setRef;
        switch (triggerEvents) {
            case 'onBlur':
                clonedProps['onBlur'] = onBlur;
                break;
            default:
                clonedProps['onBlur'] = onBlur;
                break;
        }

        const clonedElement = cloneElement(editableCellprops.children, clonedProps);

        if (editableCellprops.withError) {
            return (
                <Form form={form} component={false}>
                    <EditableContext.Provider value={form}>
                        <Form.Item
                            style={{
                                marginBottom: 0,
                                width: '100%',
                            }}
                            name={fieldName}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <span className="font-400 text-10">
                                            {editableCellprops.label} is required.
                                        </span>
                                    ),
                                },
                            ]}
                        >
                            {clonedElement}
                        </Form.Item>
                    </EditableContext.Provider>
                </Form>
            );
        } else {
            return <Fragment>{clonedElement}</Fragment>;
        }
    } else {
        return (
            <div
                ref={(ref) => ref && editableCellprops.setRef && editableCellprops.setRef(ref)}
                className="editable-cell-value-wrap cursor-pointer w-full"
                onClick={toggleEdit}
                tabIndex={editableCellprops.tabIndex || 0}
                onFocus={toggleEdit}
            >
                {editableCellprops.defaultValue || (
                    <span className="text-darwin-neutral-400">{`${editableCellprops.label}`}</span>
                )}
            </div>
        );
    }
};

export enum INVENTORY_STATUS {
    OUT_OF_STOCK = 'outOfStock',
    LESS_IN_STOCK = 'lessInStock',
    IN_STOCK = 'inStock',
}

export const findInventoryStatus = (inventory: InventoryItem): INVENTORY_STATUS => {
    if (!inventory?.in_stock) {
        return INVENTORY_STATUS.OUT_OF_STOCK;
    }

    if (inventory?.quantity <= 5) {
        return INVENTORY_STATUS.LESS_IN_STOCK;
    }

    return INVENTORY_STATUS.IN_STOCK;
};

export const InventoryStatus = (inventory: InventoryItem): JSX.Element | null => {
    if (!inventory) {
        return null;
    }

    const status = findInventoryStatus(inventory);
    const isInventory = status === INVENTORY_STATUS.OUT_OF_STOCK ? undefined : inventory;

    return (
        <AntTooltip
            getPopupContainer={() => document.getElementById(PAD_V2_CONTAINER) as HTMLElement}
            mouseEnterDelay={0.3}
            color="rgb(80, 87, 117)"
            placement="top"
            overlayInnerStyle={{
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'bg-darwin-neutral-900',
                color: 'white',
                width: 'auto',
                padding: '0',
            }}
            content={
                <div className="pb-2 px-2 font-lato">
                    {isInventory ? (
                        <>
                            <div className="flex justify-between items-center text-12 font-700">
                                <p>{isInventory?.sku} </p>
                                <p className="ml-18">Rs. {isInventory?.mrp}</p>
                            </div>
                            <div className="flex text-darwin-neutral-100 text-10 items-center mt-2">
                                IN STOCK
                            </div>
                        </>
                    ) : (
                        <div>OUT OF STOCK</div>
                    )}
                </div>
            }
        >
            <div
                className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions[status].bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions[status].hoverClass}`}
            >
                {capsulesIcon(medicinesInventoryStatusOptions[status].iconColor)}
            </div>
        </AntTooltip>
    );
};

export type CompProps = {
    kind: typeof MEDICINES_KIND | typeof LAB_TESTS_KIND;
    elements?: MedicationsEntity[];
    elementsConfig?: GeniePadElementsSettingItem[];
    medRouteEnabled: boolean;
};

interface Props extends CompProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: MedicationsEntity[];
    inventory?: Inventory;
    elementsConfig: GeniePadElementsSettingItem[];
    isNameEditable: boolean;
    containerId: string;
    upsertElement: (
        element: MedicationsEntity | LabTestsEntityOrProceduresEntity,
        index?: number,
        calculateQuantity?: boolean,
    ) => void;
    onDeleteElement?: (id: string) => void;
    onCopyDuration?: (medIndex: number) => void;
    onCopyTestOn?: (testOnId: string) => void;
    onCopyRepeatOn?: (repeatOnId: string) => void;
    onSortEnd?: (what: keyof Tool, from: number, to: number) => void;
    searchComponent?: JSX.Element;
    REF_MAP: EkaFocosableElement[][];
    hideFixNowStrip?: boolean;
    selectedClinic?: string;
    drugsSubstitute: SubstitutesResponse;
    isDoseCalculatorAvailable: boolean;
}

type State = {
    gElements?: GMedication[];
    toPopoverVisible?: number;
    soaPopoverVisible?: number;
    medInfoPopoverVisible?: number;
    medGenericPopoverVisible?: number;
    draggingIndex?: number;
    isAddDrugDialogVisible?: { medIndex: number; index: number; option: MedicationsEntity };
    activeLabTestElement?: LabTestsEntityOrProceduresEntity;
    doseCalculatorMed?: FlatMedObj;
    routePopoverVisible?: number;
};

const fuseConfig = { keys: ['name', 'actual_name'], threshold: 0.2 };

const labTestOnDefaultOption = [
    {
        label: 'Today',
        type: LAB_TESTS_FOLLOWUP_DATE_TYPE.DAY,
        value: randomString(8),
        timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE.MID_DAY,
        timeValue: '0',
    },
];

export const getStrValueAtIndex = (
    dose: MedDoseInfo,
    index: number,
    elementsConfig: GeniePadElementsSettingItem[],
): string | undefined => {
    switch (elementsConfig.filter((t) => t.isShown)[index - 1]?.id) {
        case MED_TABLE_COL_DOSE_KEY:
            return getDoseStr(dose.dose);
        case MED_TABLE_COL_FREQUENCY_KEY:
            return dose.frequency?.custom;
        case MED_TABLE_COL_TIMING_KEY:
            return dose.timing;
        case MED_TABLE_COL_DURATION_KEY:
            return dose.duration?.custom;
        case MED_TABLE_COL_START_FROM_KEY:
            return dose.start_from?.text;
        case MED_TABLE_COL_INSTRUCTION_KEY:
            return dose.instruction;
        default:
            return '';
    }
};

export const handleNextFocus = (
    medIndex: number,
    tapIndex: number,
    rowIndex: number,
    columnIndex: number,
    elements: MedicationsEntity[],
    kind: typeof MEDICINES_KIND | typeof LAB_TESTS_KIND,
    elementsConfig: GeniePadElementsSettingItem[],
    REF_MAP: EkaFocosableElement[][],
): void => {
    const element = elements[medIndex];
    if (!element) {
        return;
    }
    let dose: MedDoseInfo = element;
    if (tapIndex > -1) {
        if (!element.tapering_dose || element.tapering_dose.length < tapIndex) {
            return;
        }
        dose = element.tapering_dose[tapIndex];
    }

    let didTheJob = false;

    if (!REF_MAP[rowIndex]) {
        // handleNextFocus is called with a timeout, so we need to check if the element mounted yet ot now
        return;
    }

    if (columnIndex < 8) {
        const j = columnIndex + 1;
        if (REF_MAP[rowIndex][j]) {
            const str = getStrValueAtIndex(dose, j, elementsConfig);

            if (!str) {
                REF_MAP[rowIndex][j]?.focus();
                didTheJob = true;
                return;
            }
        }
    }

    if (!didTheJob && medIndex === elements.length - 1) {
        const t = document.getElementById(`${PRESCRIBE_V2_AS_INPUT}-${kind}`) as HTMLInputElement;
        t && t.focus();
    }
};

class MedTableRenderer extends React.Component<Props, State> {
    fuse = new Fuse([] as GMedication[], fuseConfig);
    TABLE_ID = 'med-table';

    timingOptions = PROPERTIES.med['Dose Timing'].map((item) => item.n);
    MED_FREQUENCY = PROPERTIES.med.Frequency;
    SITE_OF_APPLICATION = SITE_OF_APPLICATION;
    ROUTES = ROUTES;

    state: State = {};
    dragContainerId = randomString(4);

    componentDidMount = async (): Promise<void> => {
        // TODO types here are different
        const gElements = await getGArray<GMedication>(
            getMyDbId() || '',
            this.props.kind === MEDICINES_KIND ? DB_TABLES['medications'] : DB_TABLES['labTests'],
        );
        this.fuse = new Fuse(gElements, fuseConfig);
        this.setState({
            gElements: gElements,
        });

        getG<GMedFrequency>(getMyDbId() || '', DB_TABLES['medFrequency'], -1).then(
            (list) => (this.MED_FREQUENCY = [...this.MED_FREQUENCY, ...list]),
        );
        getG<GMedSOA>(getMyDbId() || '', DB_TABLES['medSOA'], -1).then(
            (list) => (this.SITE_OF_APPLICATION = [...this.SITE_OF_APPLICATION, ...list]),
        );
        getG<GMedTiming>(getMyDbId() || '', DB_TABLES['medTiming'], -1).then((list) => {
            const items = new Set(this.timingOptions.map((t) => t.toLowerCase().trim()));
            list.forEach((item) => {
                const isPresent = items.has(item.n.toLowerCase().trim());
                if (!isPresent) {
                    this.timingOptions.push(item.n);
                }
            });
        });
    };

    nameColumnheader = columnHeaderContent('MEDICINE', 'Generic');
    doseColumnHeader = columnHeaderContent('DOSE', 'eg. 1 tablet');
    frequencyColumnHeader = columnHeaderContent('FREQUENCY', 'eg. 1-0-1 etc');
    timingColumnHeader = columnHeaderContent('TIMING', 'eg. After meal');
    durationColumnHeader = columnHeaderContent('DURATION', 'eg. 3 days');
    quantifyColumnHeader = columnHeaderContent('QUANTITY', 'eg. 1 packet');
    startFromColumnHeader = columnHeaderContent('START FROM', 'eg. 1, 3, 5 etc');
    instructionColumnHeader = columnHeaderContent('INSTRUCTIONS', 'if any..');
    labResultNameColumnheader = columnHeaderContent('LAB RESULT');
    dateTestContent = columnHeaderContent('Test on');
    dateRepeatContent = columnHeaderContent('Repeat on');
    remarksColumns = columnHeaderContent('Remarks');

    elementSuggestApi = async (text: string): Promise<Drug[]> => {
        const { kind, selectedClinic } = this.props;
        const sType =
            kind === MEDICINES_KIND ? S_TYPE.DRUG : kind === LAB_TESTS_KIND ? S_TYPE.LAB : '';
        try {
            const res = await fetchMatchingDrugsLabTests(
                text,
                undefined,
                undefined,
                undefined,
                selectedClinic,
                undefined,
                sType,
            );
            return (kind === MEDICINES_KIND ? res.drugs : res.lab_tests) || [];
        } catch (e) {
            return [];
        }
    };

    handleSearch = async (value: string): Promise<MedicationsEntity[]> => {
        const { elements } = this.props;
        if (!value) {
            const { gElements } = this.state;
            const t = gElements
                ? [...gElements].filter((e) => !elements.find((t) => t.id === e.id))
                : [];
            t.splice(5);
            return t.map((t, index) => ({
                ...t,
                track: {
                    label: AS_INLINE_EMPTY,
                    index: index,
                    source: GENIE,
                },
            }));
        }

        let drugs: Drug[] = [];
        try {
            drugs = await this.elementSuggestApi(value);
        } catch (e) {
            console.error('elementSuggestApi', e);
        }

        const res = drugs
            .map((item) => apiDrugToMedicationEntity(item))
            .filter((e) => !elements.find((t) => t.id === e.id));

        const loweredCased = value.trim().toLowerCase();

        const all: MedicationsEntity[] = [];
        if (this.fuse) {
            all.push(
                ...this.fuse
                    .search(value)
                    .filter((e) => !elements.find((t) => t.id === e.item.id))
                    .map((t) => ({
                        ...t.item,
                        track: {
                            label: AS_INLINE_SEARCH,
                            index: all.length,
                            source: GENIE,
                        },
                    })),
            );
            for (let index = 0; index < res.length; index++) {
                if (!all.find((t) => t.id === res[index].id)) {
                    all.push({
                        ...res[index],
                        track: {
                            label: AS_INLINE_SEARCH,
                            index: all.length,
                            source: API_SEARCH,
                        },
                    });
                }
            }
        } else {
            all.push(
                ...res.map((t, index) => ({
                    ...t,
                    track: {
                        label: AS_INLINE_SEARCH,
                        index: all.length + index,
                        source: API_SEARCH,
                    },
                })),
            );
        }

        all.splice(5);

        if (
            !all[all.length - 1]?.id.startsWith(LOCAL_ID_PREFIX) &&
            res &&
            res.length > 0 &&
            res[res.length - 1].id.startsWith(LOCAL_ID_PREFIX)
        ) {
            all.push({
                ...res[res.length - 1],
                track: {
                    label: AS_INLINE_SEARCH,
                    index: all.length,
                    source: API_SEARCH,
                },
            });
        } else {
            if (!drugs) {
                if (!all.find((t) => t.name.toLowerCase() === loweredCased) && value.trim()) {
                    all.push({
                        id: `${LOCAL_ID_PREFIX}${
                            this.props.kind === MEDICINES_KIND ? 'd' : 'lt'
                        }-${new Date().getTime()}-${randomString(4)}`,
                        name: value.trim(),
                        soa: true,

                        track: {
                            label: AS_INLINE_SEARCH,
                            index: all.length,
                            source: CUSTOM,
                        },
                    });
                }
            }
        }

        return all;
    };

    renderCellAutosuggestItemJsx = (option: OptionTypeBaseObj): JSX.Element => {
        const { id, name, track } = option;
        const { kind } = this.props;

        const isAddNewEntity = id.startsWith(LOCAL_ID_PREFIX) && track?.source !== GENIE;

        return (
            <div
                className={`flex items-center justify-between ${
                    this.props.kind === MEDICINES_KIND ? 'mx-dropdown-item' : 'lx-dropdown-item'
                } cursor-pointer font-lato w-full`}
            >
                <div className="flex items-center space-x-8">
                    {isAddNewEntity ? (
                        <div className="flex items-center w-16 h-16 justify-center m-4">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.5 7C13.5 7.4375 13.1562 7.75 12.75 7.75L7.75 7.75L7.75 12.75C7.75 13.1875 7.40625 13.5313 7 13.5313C6.5625 13.5313 6.25 13.1875 6.25 12.75L6.25 7.75H1.25C0.8125 7.75 0.5 7.4375 0.5 7.03125C0.5 6.59375 0.8125 6.25 1.25 6.25H6.25V1.25C6.25 0.84375 6.5625 0.53125 7 0.53125C7.40625 0.53125 7.75 0.84375 7.75 1.25V6.25L12.75 6.25C13.1562 6.25 13.5 6.59375 13.5 7Z"
                                    fill="#004BA8"
                                />
                            </svg>
                        </div>
                    ) : (
                        <div
                            className={
                                this.props.kind === MEDICINES_KIND ? 'circle-mx' : 'circle-lx'
                            }
                        >
                            <p>{this.props.kind === MEDICINES_KIND ? 'Mx' : 'Lab'}</p>
                        </div>
                    )}
                    {isAddNewEntity ? (
                        <span className="text-14 text-darwin-primary-500">
                            Add <span className="font-700">" {name} "</span> as a{' '}
                            {kind === MEDICINES_KIND ? 'drug' : 'lab test'}{' '}
                        </span>
                    ) : (
                        <div>
                            <p className="text-neutral-1 text-14 w-full">{option.name}</p>
                            {option.generic_name && (
                                <p className="text-darwin-neutral-500 text-10 w-full">
                                    {option.generic_name}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    onElementSelect = (
        medIndex: number,
        index: number,
        option?: MedicationsEntity,
        searchString?: string,
    ): void => {
        const { elements, upsertElement, kind, elementsConfig, REF_MAP } = this.props;

        if (elements[medIndex].id === option?.id) {
            return;
        }

        if (
            kind === MEDICINES_KIND &&
            (option?.track?.source === CUSTOM || option?.track?.source === API_SEARCH)
        ) {
            if (option?.id.startsWith(LOCAL_ID_PREFIX)) {
                this.setState({ isAddDrugDialogVisible: { medIndex, index, option } });
                return;
            }
        }

        if (
            kind === MEDICINES_KIND &&
            option?.id.startsWith(LOCAL_ID_PREFIX) &&
            ((!option.form && !option.dosage_form) || !option.generic_name) &&
            !option.is_otc &&
            !option.med_cat
        ) {
            this.setState({ isAddDrugDialogVisible: { medIndex, index, option } });
            return;
        }

        upsertElement(option || { id: '', name: '' }, medIndex);

        setTimeout(() => {
            handleNextFocus(medIndex, -1, index, 0, elements, kind, elementsConfig, REF_MAP);
        }, 300);

        track('PAD_AS_SELECT', {
            inline: true.toString(),
            eid: option?.id,
            searchString: searchString,
            selectedString: option?.name,
            kind: this.props.kind,
            label: option?.track?.label,
            source: option?.track?.source,
            index: option?.track?.index?.toString(),
        });
    };

    hideAddDrugDialog = (): void => {
        this.setState({ isAddDrugDialogVisible: undefined });
    };

    onAddDrug = (option?: MedicationsEntity): void => {
        const { isAddDrugDialogVisible } = this.state;
        if (!isAddDrugDialogVisible) {
            return;
        }

        this.hideAddDrugDialog();

        if (!option) {
            return;
        }

        const { elements, upsertElement, kind, elementsConfig, REF_MAP } = this.props;
        upsertElement(option || { id: '', name: '' }, isAddDrugDialogVisible.medIndex);

        setTimeout(() => {
            handleNextFocus(
                isAddDrugDialogVisible.medIndex,
                -1,
                isAddDrugDialogVisible.index,
                0,
                elements,
                kind,
                elementsConfig,
                REF_MAP,
            );
        }, 300);
    };

    onEditDrug = (): void => {
        const { isAddDrugDialogVisible } = this.state;
        if (!isAddDrugDialogVisible) {
            return;
        }

        this.hideAddDrugDialog();

        setTimeout(() => {
            const { elements, kind, elementsConfig, REF_MAP } = this.props;

            handleNextFocus(
                isAddDrugDialogVisible.medIndex,
                -1,
                isAddDrugDialogVisible.index,
                -1,
                elements,
                kind,
                elementsConfig,
                REF_MAP,
            );
        }, 300);

        return;
    };

    onDurationSearch = async (
        text: string,
    ): Promise<{ id: string; label: string; value: string; meta: any }[]> => {
        return new Promise((resolve, _) => {
            resolve(generateDurationOptions(text));
        });
    };

    onStartFromSearch = async (
        text: string,
    ): Promise<{ id: string; label: string; value: string; meta: any }[]> => {
        return new Promise((resolve) => {
            resolve(generateStartFromOptions(text));
        });
    };

    onToDaySearch = async (
        text: string,
    ): Promise<{ id: string; label: string; value: string; meta: any }[]> => {
        return new Promise((resolve) => {
            resolve(generateToDayOptions(text));
        });
    };

    onSoaSearch = async (text: string): Promise<{ id: string; name: string }[]> => {
        return new Promise((resolve) => {
            if (!text) {
                resolve([]);
                return;
            }
            const loweredText = text.toLowerCase();
            const options = this.SITE_OF_APPLICATION.filter((t) =>
                t.name?.toLowerCase().includes(loweredText),
            );
            if (text && !options.find((t) => t.name.toLowerCase() === loweredText)) {
                options.push({
                    name: text,
                    id: generateElementId(loweredText),
                });
            }
            resolve(options);
        });
    };

    onRouteSearch = async (text: string): Promise<{ id: string; name: string }[]> => {
        return new Promise((resolve) => {
            if (!text) {
                resolve([
                    ...this.ROUTES.map((route) => ({
                        name: route.display_name,
                        id: route.id,
                    })),
                ]);
                return;
            }
            const loweredText = text.toLowerCase();
            const options = this.ROUTES.filter(
                (t) =>
                    t.name?.toLowerCase().includes(loweredText) ||
                    t.display_name?.toLowerCase().includes(loweredText),
            ).map((route) => ({
                name: route.display_name,
                id: route.id,
            }));
            resolve(options);
        });
    };

    onDoseSearch = (
        mxIndex: number,
    ): ((
        value: string,
    ) => Promise<{ id: string; label: string; value: string; meta: any }[]>) => async (
        text: string,
    ): Promise<{ id: string; label: string; value: string; meta: any }[]> => {
        const element = this.props.elements[mxIndex];

        if (!element) {
            return new Promise((resolve) => {
                resolve([]);
            });
        }

        const df_id = element.df_id;
        const form = element.form;
        const dosage_form = element.dosage_form;

        const mxDoseFormsUnit = df_id ? MX_DOSAGE_FORM_UNIT[df_id] : undefined;

        let options: { label: string; value: string; meta: any }[] = [];

        if (!mxDoseFormsUnit) {
            // try to resolve from Form/dosage form
            const t =
                MX_DOSAGE_FORM_UNIT[
                    FORM_TO_DF_MAPPING[form?.toLowerCase() || dosage_form?.toLowerCase() || ''] ||
                        ''
                ];

            if (t) {
                options = generateDoseOptions(t?.unit_list, t?.name)(text);
            } else {
                // populate all options
                options = generateDoseOptions(
                    ALL_FORM_UNITS_FOR_MX,
                    '',
                )(text).filter((t) => t.label.toLowerCase().includes(text.toLowerCase()));
            }
        } else {
            options = generateDoseOptions(mxDoseFormsUnit.unit_list, mxDoseFormsUnit.name)(text);
        }

        return new Promise((resolve) => {
            resolve(options.map((t) => ({ ...t, id: t.label })));
        });
    };

    getSelectedDoseValue = (
        mxIndex: number,
        tapIndex: number,
    ): { id: string; label: string; value: string; meta: any } | undefined => {
        const element = this.props.elements[mxIndex];

        if (!element) {
            return undefined;
        }

        let dose: MedDose | undefined;
        if (tapIndex === -1) {
            dose = element.dose;
        } else {
            dose =
                (element.tapering_dose &&
                    element.tapering_dose[tapIndex] &&
                    element.tapering_dose[tapIndex].dose) ||
                undefined;
        }

        const doseDynamicSuggestValue =
            dose && dose?.id && dose.value && dose.unit
                ? {
                      id: `${dose.value} ${dose.unit}`,
                      label: `${dose.value} ${dose.unit}`,
                      value: dose.value,
                      meta: {
                          unit: dose.unit,
                          id: dose.id,
                      },
                  }
                : dose?.custom
                ? {
                      id: dose?.custom,
                      label: dose?.custom,
                      value: dose?.custom,
                      meta: {},
                  }
                : undefined;

        return doseDynamicSuggestValue;
    };

    onDoseValueChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; label: string; value: string; meta: any },
    ): void => {
        const element = this.props.elements[medIndex];

        if (!element) {
            return;
        }

        const df_id = element.df_id;

        const mxDoseFormsUnit = df_id ? MX_DOSAGE_FORM_UNIT[df_id] : undefined;

        if (!mxDoseFormsUnit) {
            const newDose = option
                ? {
                      custom: '',
                      unit: option.meta.unit,
                      value: option.value,
                      id: option.meta.id,
                  }
                : {};

            if (tapIndex === -1) {
                this.props.upsertElement({
                    ...element,
                    form: option ? undefined : element.form,
                    dosage_form: option
                        ? element.dosage_form || option.meta.formName
                        : element.dosage_form,
                    dose: newDose,
                });
            } else {
                const { tapering_dose } = element;
                if (tapering_dose && tapering_dose.length > tapIndex) {
                    tapering_dose[tapIndex].dose = newDose;

                    this.props.upsertElement({ ...element });
                }
            }
        } else {
            const newDose = option
                ? {
                      custom: '',
                      id: option.meta.id,
                      value: option.value,
                      unit: option.meta.unit,
                  }
                : undefined;

            if (tapIndex === -1) {
                !option
                    ? this.props.upsertElement({
                          ...element,
                          form: undefined,
                          dose: newDose,
                      })
                    : this.props.upsertElement({
                          ...element,
                          form: undefined,
                          // this is in coded format now
                          dosage_form: element.dosage_form || option.meta.formName,
                          dose: newDose,
                      });
            } else {
                const { tapering_dose } = element;
                if (tapering_dose && tapering_dose.length > tapIndex) {
                    tapering_dose[tapIndex].dose = newDose;
                    this.props.upsertElement({ ...element });
                }
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Dose Value',
            action: option?.value ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onDurationValueChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; label: string; value: string; meta: any },
    ): void => {
        const element = this.props.elements[medIndex];
        const duration = option
            ? {
                  unit: getUnit(option?.meta.unit, option?.meta.input),
                  value: option?.meta.input,
                  custom: option ? option?.label : '',
              }
            : undefined;

        if (tapIndex === -1) {
            this.props.upsertElement({ ...element, duration });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].duration = duration;

                this.props.upsertElement({ ...element });
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Duration',
            action: option?.value ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onQuantityValueChange = (medIndex: number, tapIndex: number, option?: string): void => {
        const { upsertElement } = this.props;
        const element = this.props.elements[medIndex];

        const quantity = { custom: option || '' };

        if (tapIndex === -1) {
            upsertElement({ ...element, quantity }, undefined, true);
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].quantity = quantity;
                upsertElement({ ...element }, undefined, true);
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Quantity',
            action: option ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onStartFromChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; label: string; value: string; meta: any },
    ): void => {
        const element = this.props.elements[medIndex];

        let startFrom: { unit: string; value: string; text: string } | undefined;

        if (option) {
            startFrom = { unit: option.meta.unit, value: option.meta.input, text: option.label };
        }

        if (tapIndex === -1) {
            this.props.upsertElement({
                ...element,
                start_from: startFrom,
                to_day: startFrom ? element.to_day : undefined,
            });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].start_from = startFrom;
                tapering_dose[tapIndex].to_day = startFrom
                    ? tapering_dose[tapIndex].to_day
                    : undefined;
                this.props.upsertElement({ ...element });
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Start From',
            action: option?.value ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onInstructionBlur = (medIndex: number, tapIndex: number, option?: string): void => {
        const element = this.props.elements[medIndex];

        if (this.props.kind === MEDICINES_KIND) {
            if (tapIndex === -1) {
                this.props.upsertElement({
                    ...element,
                    instruction: option,
                });
            } else {
                const { tapering_dose } = element;
                if (tapering_dose && tapering_dose.length > tapIndex) {
                    tapering_dose[tapIndex].instruction = option;
                    this.props.upsertElement({ ...element });
                }
            }
        } else {
            this.props.upsertElement({ ...element, remark: option });
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Instruction',
            action: option ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: this.props.kind,
        });
    };

    onToDayChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; label: string; value: string; meta: any },
    ): void => {
        const element = this.props.elements[medIndex];

        let toDay: { unit: string; value: string; text: string } | undefined;

        if (option) {
            toDay = { unit: option.meta.unit, value: option.meta.input, text: option.label };
        }

        if (tapIndex === -1) {
            this.props.upsertElement({
                ...element,
                to_day: toDay,
            });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].to_day = toDay;
                this.props.upsertElement({ ...element });
            }
        }

        this.setState({
            toPopoverVisible: undefined,
        });

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'To Day',
            action: option?.value ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onAreaChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; name: string },
        routeValue?: string,
    ): void => {
        const element = this.props.elements[medIndex];

        let area: { id: string; name: string } | undefined;

        if (option) {
            area = option;
        }

        if (tapIndex === -1) {
            this.props.upsertElement({
                ...element,
                area: area,
            });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].area = area;
                this.props.upsertElement({ ...element });
            }
        }
        if (this.props.medRouteEnabled) {
            if (routeValue) {
                this.setState({
                    routePopoverVisible: undefined,
                });
            }
        } else {
            this.setState({
                soaPopoverVisible: undefined,
            });
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Area',
            action: option?.name ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onRouteChange = (
        medIndex: number,
        tapIndex: number,
        option?: { id: string; name: string },
        soaValue?: string,
    ): void => {
        const element = this.props.elements[medIndex];

        let route: MedRoute | undefined;

        const selectedRoute = this.ROUTES.find((r) => r.id === option?.id);

        if (selectedRoute) {
            route = selectedRoute;
        }

        if (!route) {
            if (tapIndex === -1) {
                this.props.upsertElement({
                    ...element,
                    route: undefined,
                });
            } else {
                const { tapering_dose } = element;
                if (tapering_dose && tapering_dose.length > tapIndex) {
                    tapering_dose[tapIndex].route = undefined;
                    this.props.upsertElement({ ...element });
                }
            }
            return;
        }

        if (tapIndex === -1) {
            this.props.upsertElement({
                ...element,
                route: route,
            });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].route = route;
                this.props.upsertElement({ ...element });
            }
        }
        if (soaValue) {
            this.setState({
                routePopoverVisible: undefined,
            });
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Route',
            action: option?.name ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onFrequencyValueChange = (medIndex: number, tapIndex: number, option?: string): void => {
        const element = this.props.elements[medIndex];

        const newFrequency = {
            type: 'custom',
            custom: option,
        };

        if (tapIndex === -1) {
            this.props.upsertElement({ ...element, frequency: newFrequency });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].frequency = newFrequency;
                this.props.upsertElement({ ...element });
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Frequency',
            action: option ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onTimingValueChange = (medIndex: number, tapIndex: number, option?: string): void => {
        const element = this.props.elements[medIndex];

        const newTiming = option || '';

        if (tapIndex === -1) {
            this.props.upsertElement({
                ...element,
                timing: newTiming,
            });
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose[tapIndex].timing = newTiming;
                this.props.upsertElement({ ...element });
            }
        }

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Timing',
            action: option ? 'SET' : 'UNSET',
            isTap: new String(tapIndex > 0).toString(),
            kind: MEDICINES_KIND,
        });
    };

    onDeleteRow = (medIndex: number, tapIndex: number): void => {
        const element = this.props.elements[medIndex];

        if (tapIndex === -1) {
            this.props.onDeleteElement?.(element.id);
        } else {
            const { tapering_dose } = element;
            if (tapering_dose && tapering_dose.length > tapIndex) {
                tapering_dose.splice(tapIndex, 1);
                this.props.upsertElement({ ...element });
            }

            track('PX_ELEMENT_PROPERTY', {
                // eId: data.id,
                eName: element?.name || '',
                // pId: p.id,
                pName: 'Tap Dose',
                action: 'DELETE',
                kind: MEDICINES_KIND,
            });
        }
    };

    onAddDose = (medIndex: number) => {
        const { elements } = this.props;
        const element = elements[medIndex];
        if (!element) {
            return;
        }

        if (element.tapering_dose) {
            element.tapering_dose.push({});
        } else {
            element.tapering_dose = [{}];
        }

        this.props.upsertElement({ ...element });

        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: element?.name || '',
            // pId: p.id,
            pName: 'Tap Dose',
            action: 'ADD',
            kind: MEDICINES_KIND,
        });
    };

    saveMedInfo = (medIndex: number, form: string, generic: string) => {
        const element = this.props.elements[medIndex];

        if (!element) {
            return;
        }

        this.props.upsertElement({
            ...element,
            form: form,
            dosage_form: undefined,
            df_id: FORM_TO_DF_MAPPING?.[form],
            dose: { custom: !form ? '' : element.dose?.custom },
            generic_name: generic,
        });

        this.setState({
            medInfoPopoverVisible: undefined,
            medGenericPopoverVisible: undefined,
        });
    };

    onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const { onSortEnd } = this.props;
        this.setState({
            draggingIndex: undefined,
        });

        const medIndex = this.flatElArray();

        if (oldIndex !== newIndex) {
            onSortEnd?.(
                this.props.kind === MEDICINES_KIND ? 'medications' : 'labTests',
                this.props.kind === MEDICINES_KIND ? medIndex[oldIndex] : oldIndex,
                this.props.kind === MEDICINES_KIND ? medIndex[newIndex] : newIndex,
            );
        }
    };

    onSortStart = (params: SortStart) => {
        const medIndex = this.flatElArray();

        handleTableDragWidth(params, this.dragContainerId);

        this.setState({
            draggingIndex: medIndex[params.index],
        });
    };

    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    DraggableContainer = (props) => {
        return (
            <SortableContainer
                id={this.dragContainerId}
                useDragHandle
                helperClass="relative"
                helperContainer={() => document.getElementById(this.dragContainerId)}
                onSortEnd={this.onSortEnd}
                onSortStart={this.onSortStart}
                {...props}
            />
        );
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        const { REF_MAP } = this.props;
        if (!REF_MAP[elIndex]) {
            REF_MAP[elIndex] = [];
        }

        REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    DraggableBodyRow = ({
        className,
        ...restProps
    }: {
        children: ReactNode;
        className: string;
        'data-row-key': string;
        style: React.StyleHTMLAttributes<HTMLDivElement>;
    }) => {
        const { elements, kind } = this.props;
        const indexX =
            kind === MEDICINES_KIND
                ? elements
                      .reduce((arr, el) => {
                          arr.push(el.id);
                          el.tapering_dose &&
                              arr.push(...new Array(el.tapering_dose?.length).fill(el.id));
                          return arr;
                      }, [] as string[])
                      .findIndex(
                          (x, index) =>
                              `${kind}-${index + (getUncodedDrugs(elements).length ? 1 : 0)}` ===
                              restProps['data-row-key'],
                      )
                : elements.findIndex(
                      (x, index) => `${kind}-${index}` === restProps['data-row-key'],
                  );

        return (
            <SortableItem className={className || 'editable-row'} index={indexX} {...restProps} />
        );
    };

    components = {
        body: {
            wrapper: this.DraggableContainer,
            row: this.DraggableBodyRow,
        },
    };

    flatElArray = (): number[] => {
        const { elements } = this.props;

        return elements.reduce((arr, el, index) => {
            arr.push(index);
            el.tapering_dose && arr.push(...new Array(el.tapering_dose?.length).fill(index));
            return arr;
        }, [] as number[]);
    };

    getMedInstructionColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex } = this.state;
        const { REF_MAP } = this.props;
        return (
            <Column
                className={`p-5 ${'mx-instructions'}`}
                // fixed="right"
                title={this.instructionColumnHeader}
                onHeaderCell={() => ({
                    style: {
                        backgroundColor: 'rgba(244, 246, 249, var(--tw-bg-opacity))',
                        padding: '8px 12px',
                    },
                })}
                key="instruction"
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig } = this.props;
                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    const value = el.instruction;

                    return {
                        children: (
                            <div className="flex space-x-8 items-center">
                                <EditableCellV2
                                    defaultValue={value}
                                    onBlur={(e) => {
                                        this.onInstructionBlur(
                                            el.medIndex,
                                            el.tapIndex,
                                            e.target.value,
                                        );
                                    }}
                                    setRef={(ref) => {
                                        this.setPropertyInputRef(ref, index, columnIndex);
                                        return;
                                    }}
                                    label="Instructions"
                                >
                                    <Input.TextArea
                                        autoFocus
                                        defaultValue={value}
                                        rows={2}
                                        placeholder="Add notes here"
                                        onBlur={(e) => {
                                            this.onInstructionBlur(
                                                el.medIndex,
                                                el.tapIndex,
                                                e.target.value,
                                            );
                                            if (e.target.value) {
                                                handleNextFocus(
                                                    el.medIndex,
                                                    el.tapIndex,
                                                    index,
                                                    columnIndex,
                                                    elements,
                                                    kind,
                                                    elementsConfig,
                                                    REF_MAP,
                                                );
                                            }
                                        }}
                                    />
                                </EditableCellV2>
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    getStartFromColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex, toPopoverVisible } = this.state;
        const { containerId } = this.props;
        return (
            <Column
                className="p-5 mx-startfrom"
                title={this.startFromColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP } = this.props;
                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    return {
                        children: (
                            <div className="w-full space-y-4">
                                <EditableCell
                                    containerId={containerId}
                                    withoutError
                                    label="Start From"
                                    fieldName={`msf-${index}`}
                                    setRef={(ref) => {
                                        this.setPropertyInputRef(ref, index, columnIndex);
                                        return;
                                    }}
                                    onSearch={this.onStartFromSearch}
                                    selectedValue={
                                        flatArray[index].start_from?.value !== undefined
                                            ? {
                                                  label: flatArray[index].start_from?.text || '',
                                                  value: flatArray[index].start_from?.value || '',
                                                  id: flatArray[index].start_from?.text || '',
                                                  meta: {},
                                              }
                                            : undefined
                                    }
                                    debounceDelay={0}
                                    getLabelToCompare={(o) => o.label}
                                    customDropdownWidth={120}
                                    placeholder={'eg: 3 day'}
                                    onSelect={(option) => {
                                        this.onStartFromChange(el.medIndex, el.tapIndex, option);
                                        if (option && option.label) {
                                            handleNextFocus(
                                                el.medIndex,
                                                el.tapIndex,
                                                index,
                                                columnIndex,
                                                elements,
                                                kind,
                                                elementsConfig,
                                                REF_MAP,
                                            );
                                        }
                                    }}
                                />
                                {flatArray[index].start_from?.value && (
                                    <Popover
                                        placement="left"
                                        title={'Till when?'}
                                        onVisibleChange={(v) =>
                                            this.setState({
                                                toPopoverVisible: v ? index : undefined,
                                            })
                                        }
                                        visible={toPopoverVisible === index}
                                        content={
                                            <AntAutoCompleteV2
                                                autoFocus
                                                setRef={() => {
                                                    return;
                                                }}
                                                onSearch={this.onToDaySearch}
                                                selectedValue={
                                                    flatArray[index].to_day?.value !== undefined
                                                        ? {
                                                              label:
                                                                  flatArray[index].to_day?.text ||
                                                                  '',
                                                              value:
                                                                  flatArray[index].to_day?.value ||
                                                                  '',
                                                              id:
                                                                  flatArray[index].to_day?.text ||
                                                                  '',
                                                              meta: {},
                                                          }
                                                        : undefined
                                                }
                                                debounceDelay={0}
                                                getLabelToCompare={(o) => o.label}
                                                placeholder={'Till when'}
                                                customDropdownWidth={120}
                                                onSelect={(option) =>
                                                    this.onToDayChange(
                                                        el.medIndex,
                                                        el.tapIndex,
                                                        option,
                                                    )
                                                }
                                            />
                                        }
                                        trigger="click"
                                    >
                                        <div className="border border-transparent hover:border-darwin-neutral-200 cursor-pointer py-4 rounded-2 px-8">
                                            <span className="text-actionPrimary font-700">
                                                {flatArray[index].to_day?.text
                                                    ? `${flatArray[index].to_day?.text}`
                                                    : 'To'}
                                            </span>
                                        </div>
                                    </Popover>
                                )}
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    getDurationColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex } = this.state;
        const { containerId, onCopyDuration } = this.props;
        return (
            <Column
                className="p-5 mx-duration relative"
                title={this.durationColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP } = this.props;

                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    return {
                        children: (
                            <div className="flex flex-col">
                                <div className="w-full">
                                    <EditableCell
                                        containerId={containerId}
                                        withoutError
                                        label="Duration"
                                        fieldName={`md-${index}`}
                                        setRef={(r) => {
                                            this.setPropertyInputRef(r, index, columnIndex);
                                        }}
                                        onSearch={this.onDurationSearch}
                                        selectedValue={
                                            flatArray[index].duration?.custom !== undefined
                                                ? {
                                                      label:
                                                          flatArray[index].duration?.custom || '',
                                                      value:
                                                          flatArray[index].duration?.custom || '',
                                                      id: flatArray[index].duration?.custom || '',
                                                      meta: {},
                                                  }
                                                : undefined
                                        }
                                        debounceDelay={0}
                                        getLabelToCompare={(o) => o.label}
                                        placeholder={'eg: 7 days'}
                                        customDropdownWidth={120}
                                        onSelect={(option) => {
                                            this.onDurationValueChange(
                                                el.medIndex,
                                                el.tapIndex,
                                                option,
                                            );
                                            if (option && option.label) {
                                                handleNextFocus(
                                                    el.medIndex,
                                                    el.tapIndex,
                                                    index,
                                                    columnIndex,
                                                    elements,
                                                    kind,
                                                    elementsConfig,
                                                    REF_MAP,
                                                );
                                            }
                                        }}
                                    />
                                </div>

                                {onCopyDuration && el.tapIndex === -1 && (
                                    <CopyToAll
                                        id={`copy-duration-v2-svg-${el.medIndex}`}
                                        onClick={() => onCopyDuration(el.medIndex)}
                                        tableId={this.TABLE_ID}
                                    />
                                )}
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    getQuantityColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex } = this.state;

        return (
            <Column
                className="p-5 mx-duration relative"
                title={this.quantifyColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP } = this.props;

                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    return {
                        children: (
                            <div className="flex flex-col">
                                <EditableCellV2
                                    fieldName={`efc-quantity-${index}`}
                                    defaultValue={flatArray[index].quantity?.custom || ''}
                                    onBlur={(e) => {
                                        this.onQuantityValueChange(
                                            el.medIndex,
                                            el.tapIndex,
                                            e.target.value,
                                        );
                                        if (e.target.value) {
                                            handleNextFocus(
                                                el.medIndex,
                                                el.tapIndex,
                                                index,
                                                columnIndex,
                                                elements,
                                                kind,
                                                elementsConfig,
                                                REF_MAP,
                                            );
                                        }
                                    }}
                                    label="quantity"
                                >
                                    <Input
                                        autoFocus
                                        defaultValue={flatArray[index].quantity?.custom || ''}
                                        placeholder="quantity"
                                        allowClear
                                    />
                                </EditableCellV2>
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    onTimingSearch = (value: string): Promise<string[]> => {
        return onTimingSearch(value, this.timingOptions);
    };

    getTimingColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex } = this.state;
        const { containerId } = this.props;
        return (
            <Column
                className="p-5 mx-timing"
                title={this.timingColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP } = this.props;
                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    return {
                        children: (
                            <EditableCellV2
                                defaultValue={flatArray[index].timing}
                                setRef={(ref) => {
                                    this.setPropertyInputRef(ref, index, columnIndex);
                                    return;
                                }}
                                label="Timing"
                            >
                                <AntAutoCompleteV2
                                    autoFocus
                                    onSearch={this.onTimingSearch}
                                    selectedValue={flatArray[index].timing || ''}
                                    debounceDelay={200}
                                    getLabelToCompare={(o) => o}
                                    placeholder={'Timing'}
                                    customDropdownWidth={180}
                                    onSelect={(option) => {
                                        this.onTimingValueChange(el.medIndex, el.tapIndex, option);
                                        if (option) {
                                            handleNextFocus(
                                                el.medIndex,
                                                el.tapIndex,
                                                index,
                                                columnIndex,
                                                elements,
                                                kind,
                                                elementsConfig,
                                                REF_MAP,
                                            );
                                        }
                                    }}
                                    containerIdToAttachDropdown={containerId}
                                    key={`${index}-${columnIndex}`}
                                />
                            </EditableCellV2>
                        ),
                    };
                }}
            />
        );
    };

    onFrequencySearch = (value: string): Promise<string[]> => {
        return onFrequencySearch(value, this.MED_FREQUENCY);
    };

    getFrequencyColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { draggingIndex } = this.state;
        const { containerId } = this.props;
        return (
            <Column
                className="p-5 mx-frequency"
                title={this.frequencyColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP } = this.props;
                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    return {
                        children: (
                            <div className="hover-parent-element">
                                <EditableCellV2
                                    fieldName={`mf-${index}`}
                                    defaultValue={flatArray[index].frequency?.custom || ''}
                                    setRef={(ref) => {
                                        this.setPropertyInputRef(ref, index, columnIndex);
                                        return;
                                    }}
                                    label="Frequency"
                                >
                                    <AntAutoCompleteV2
                                        autoFocus
                                        onSearch={this.onFrequencySearch}
                                        selectedValue={flatArray[index].frequency?.custom || ''}
                                        debounceDelay={200}
                                        getLabelToCompare={(o) => o}
                                        placeholder={'Frequency'}
                                        customDropdownWidth={180}
                                        onSelect={(option) => {
                                            this.onFrequencyValueChange(
                                                el.medIndex,
                                                el.tapIndex,
                                                option,
                                            );
                                            if (option) {
                                                handleNextFocus(
                                                    el.medIndex,
                                                    el.tapIndex,
                                                    index,
                                                    columnIndex,
                                                    elements,
                                                    kind,
                                                    elementsConfig,
                                                    REF_MAP,
                                                );
                                            }
                                        }}
                                        containerIdToAttachDropdown={containerId}
                                        key={`${index}-${columnIndex}`}
                                    />
                                </EditableCellV2>
                                {isOnboardedForPharma() && (
                                    <p className="hover-child-element absolute left-6 bottom-6 text-7 text-darwin-status-warning-500 font-700">
                                        * Mandatory for orders
                                    </p>
                                )}
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    getDoseColumn = (flatArray: FlatMedObj[], columnIndex: number) => {
        const { soaPopoverVisible, draggingIndex, routePopoverVisible } = this.state;
        const { containerId, isDoseCalculatorAvailable } = this.props;

        return (
            <Column
                className="mx-dose p-5"
                title={this.doseColumnHeader}
                onHeaderCell={headerStyle}
                onCell={(record: FlatMedObj, rowIndex?: number) => {
                    if (rowIndex == undefined) {
                        return {};
                    }
                    if (record.id === 'add-drug') {
                        return {
                            className: 'hidden',
                        };
                    }
                    return {};
                }}
                render={(_, el: FlatMedObj, index) => {
                    const { elements, kind, elementsConfig, REF_MAP, medRouteEnabled } = this.props;
                    if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                    if (el.id === 'add-drug') {
                        return null;
                    }

                    const medRouteValue = flatArray[index].route?.display_name;
                    const soaValue = flatArray[index].area?.name;

                    return {
                        children: (
                            <div id={`med-route-popover-${index}`} className="hover-parent-element">
                                <div>
                                    <EditableCell
                                        containerId={containerId}
                                        withoutError
                                        label="Dose"
                                        fieldName={`mdo-${index}`}
                                        setRef={(ref) => {
                                            this.setPropertyInputRef(ref, index, columnIndex);
                                        }}
                                        customDropdownWidth={150}
                                        debounceDelay={0}
                                        onSearch={(text) => this.onDoseSearch(el.medIndex)(text)}
                                        selectedValue={this.getSelectedDoseValue(
                                            el.medIndex,
                                            el.tapIndex,
                                        )}
                                        getLabelToCompare={(o) => o.label}
                                        placeholder={'e.g 1 Tablet'}
                                        onSelect={(option) => {
                                            this.onDoseValueChange(
                                                el.medIndex,
                                                el.tapIndex,
                                                option,
                                            );
                                            if (option && option.label) {
                                                handleNextFocus(
                                                    el.medIndex,
                                                    el.tapIndex,
                                                    index,
                                                    columnIndex,
                                                    elements,
                                                    kind,
                                                    elementsConfig,
                                                    REF_MAP,
                                                );
                                            }
                                        }}
                                    />
                                    {medRouteEnabled && (
                                        <Popover
                                            placement="right"
                                            title={'Route?'}
                                            getPopupContainer={() =>
                                                document.getElementById(
                                                    `med-route-popover-${index}`,
                                                ) || document.body
                                            }
                                            onVisibleChange={(v) =>
                                                this.setState({
                                                    routePopoverVisible: v ? index : undefined,
                                                })
                                            }
                                            visible={routePopoverVisible === index}
                                            content={
                                                <>
                                                    <AntAutoCompleteV2
                                                        containerIdToAttachDropdown={containerId}
                                                        placement="bottomLeft"
                                                        autoFocus
                                                        setRef={() => {
                                                            return;
                                                        }}
                                                        onSearch={this.onRouteSearch}
                                                        selectedValue={
                                                            flatArray[index].route !== undefined
                                                                ? {
                                                                      id: flatArray[index].route.id,
                                                                      name:
                                                                          flatArray[index].route
                                                                              .display_name,
                                                                  }
                                                                : undefined
                                                        }
                                                        debounceDelay={0}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder={'Route'}
                                                        onSelect={(option) =>
                                                            this.onRouteChange(
                                                                el.medIndex,
                                                                el.tapIndex,
                                                                option,
                                                                soaValue,
                                                            )
                                                        }
                                                    />
                                                    <div className="p-10" />
                                                    <AntAutoCompleteV2
                                                        // autoFocus
                                                        containerIdToAttachDropdown={containerId}
                                                        setRef={() => {
                                                            return;
                                                        }}
                                                        onSearch={this.onSoaSearch}
                                                        selectedValue={
                                                            flatArray[index].area !== undefined
                                                                ? flatArray[index].area
                                                                : undefined
                                                        }
                                                        debounceDelay={0}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder={'Site'}
                                                        onSelect={(option) =>
                                                            this.onAreaChange(
                                                                el.medIndex,
                                                                el.tapIndex,
                                                                option,
                                                                medRouteValue,
                                                            )
                                                        }
                                                    />
                                                </>
                                            }
                                            trigger="click"
                                        >
                                            <div className="border border-transparent hover:border-darwin-neutral-200 cursor-pointer py-4 rounded-2 px-8">
                                                <span className="text-actionPrimary font-500 flex flex-col ">
                                                    {medRouteValue || soaValue ? (
                                                        <>
                                                            <span>
                                                                {medRouteValue || ''}
                                                                {medRouteValue && soaValue
                                                                    ? ', '
                                                                    : ''}
                                                            </span>
                                                            <span>{soaValue || ''}</span>
                                                        </>
                                                    ) : (
                                                        `Route:`
                                                    )}
                                                </span>
                                            </div>
                                        </Popover>
                                    )}

                                    {!medRouteEnabled &&
                                        (flatArray[index].soa ||
                                            flatArray[index].id.startsWith(LOCAL_ID_PREFIX)) && (
                                            <Popover
                                                placement="right"
                                                title={'Site of application?'}
                                                onVisibleChange={(v) =>
                                                    this.setState({
                                                        soaPopoverVisible: v ? index : undefined,
                                                    })
                                                }
                                                // getPopupContainer={() =>
                                                //     containerId
                                                //         ? (document.getElementById(
                                                //               containerId,
                                                //           ) as HTMLElement)
                                                //         : document.body
                                                // }
                                                visible={soaPopoverVisible === index}
                                                content={
                                                    <AntAutoCompleteV2
                                                        autoFocus
                                                        setRef={() => {
                                                            return;
                                                        }}
                                                        onSearch={this.onSoaSearch}
                                                        selectedValue={
                                                            flatArray[index].area !== undefined
                                                                ? flatArray[index].area
                                                                : undefined
                                                        }
                                                        debounceDelay={0}
                                                        getLabelToCompare={(o) => o.name}
                                                        placeholder={'SOA'}
                                                        onSelect={(option) =>
                                                            this.onAreaChange(
                                                                el.medIndex,
                                                                el.tapIndex,
                                                                option,
                                                            )
                                                        }
                                                        // containerIdToAttachDropdown={containerId}
                                                    />
                                                }
                                                trigger="click"
                                            >
                                                <div className="border border-transparent hover:border-darwin-neutral-200 cursor-pointer py-4 rounded-2 px-8">
                                                    <span className="text-actionPrimary font-500">
                                                        {flatArray[index].area?.name || (
                                                            <span className="">Apply:</span>
                                                        )}
                                                    </span>
                                                </div>
                                            </Popover>
                                        )}
                                    {isDoseCalculatorAvailable && (
                                        <DoseCalculator
                                            onClick={() => {
                                                this.setState({
                                                    doseCalculatorMed: el,
                                                });
                                                track('OPEN_DOSE_CALCULATOR', {
                                                    medId: el.id,
                                                    medName: el.name,
                                                });
                                            }}
                                        />
                                    )}
                                </div>
                                {isOnboardedForPharma() && (
                                    <p className="hover-child-element absolute left-6 bottom-6 text-7 text-darwin-status-warning-500 font-700">
                                        * Mandatory for orders
                                    </p>
                                )}
                            </div>
                        ),
                    };
                }}
            />
        );
    };

    orderMedicinesStatusText = (element: FlatMedObj): JSX.Element | null => {
        const { hxng_status } = element;

        if (!hxng_status) return null;

        return hxng_status === 'available' ? (
            <div
                className={`px-4 py-2 mt-4 rounded-4 ${medicinesInventoryStatusOptions.inStock.bg}`}
                style={{ width: 'fit-content' }}
            >
                <p
                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.inStock.textColor}`}
                >
                    {medicinesInventoryStatusOptions.inStock.textFoxHxng}
                </p>
            </div>
        ) : (
            <div
                className={`px-4 py-2 mt-4 rounded-4 ${medicinesInventoryStatusOptions.outOfStock.bg}`}
                style={{ width: 'fit-content' }}
            >
                <p
                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.outOfStock.textColor} capitalize`}
                >
                    {hxng_status?.split('_').join(' ') || 'Not Available'}
                </p>
            </div>
        );
    };

    substitutesStatusIcon = (element: FlatMedObj): JSX.Element | undefined => {
        const { drugsSubstitute, dispatch } = this.props;
        const currentMedicine = drugsSubstitute.find((i) => i[0]?.eka_id === element?.id);
        return (
            currentMedicine && (
                <AntTooltip content="Substitutes available" placement="bottom">
                    <div
                        className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions.inStock.bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions.inStock.hoverClass}`}
                        onClick={() => dispatch(showDialog('substitutions'))}
                    >
                        {capsulesIcon(medicinesInventoryStatusOptions.inStock.iconColor)}
                    </div>
                </AntTooltip>
            )
        );
    };

    bookLabTestsStatusText = (element: LabTestsEntityOrProceduresEntity): JSX.Element => {
        return element.book ? (
            <div
                className={`px-4 py-2 mt-4 rounded-4 ${medicinesInventoryStatusOptions.inStock.bg}`}
                style={{ width: 'fit-content' }}
            >
                <p
                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.inStock.textColor}`}
                >
                    {medicinesInventoryStatusOptions.inStock.textFoxLabs}
                </p>
            </div>
        ) : (
            <div
                className={`px-4 py-2 mt-4 rounded-4 ${medicinesInventoryStatusOptions.outOfStock.bg}`}
                style={{ width: 'fit-content' }}
            >
                <p
                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.outOfStock.textColor}`}
                >
                    {medicinesInventoryStatusOptions.outOfStock.textFoxLabs}
                </p>
            </div>
        );
    };

    bookLabTestsStatusIcon = (status: 'outOfStock' | 'inStock'): JSX.Element => {
        return (
            <div
                className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions[status].bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions[status].hoverClass}`}
            >
                {labsIcon(medicinesInventoryStatusOptions[status].iconColor)}
            </div>
        );
    };

    onRename = (index: number, newName: string): void => {
        const { elements } = this.props;
        const element = elements[index];

        if (element.name === newName) return;

        element.actual_name = element.actual_name || element.name;
        element.name = newName;

        this.props.upsertElement({ ...element });
    };

    onReset = (index: number): void => {
        const { elements } = this.props;
        const element = elements[index];

        element.name = element.actual_name || element.name;
        element.actual_name = undefined;

        this.props.upsertElement({ ...element });
    };

    focusDrugName = (elId: string): void => {
        setTimeout(() => {
            const { elements, kind, elementsConfig, REF_MAP } = this.props;
            const flatArray = getFlatArray(elements, kind);
            const rowIndex = flatArray.findIndex((t) => t.id === elId);
            const medIndex = elements.findIndex((t) => t.id === elId);

            handleNextFocus(medIndex, -1, rowIndex, -1, elements, kind, elementsConfig, REF_MAP);
        }, 300);
    };

    saveDrugs = (drugs?: FlatMedObj[]): void => {
        const { upsertElement, elements } = this.props;
        if (!drugs) {
            return;
        }
        batch(() => {
            drugs.forEach((drug) => {
                const alreadyPresent = elements.find((el) => el.id === drug.id);
                if (alreadyPresent) {
                    upsertElement(
                        {
                            ...alreadyPresent,
                            generic_name: drug.generic_name,
                            name: drug.name,
                            generic_id: drug.generic_id,
                            form: drug.form,
                            dosage_form: drug.dosage_form,
                            manufacturer_name: drug.manufacturer_name,
                            hxng_mapped: drug.hxng_mapped,
                            hxng_status: drug.hxng_status,
                            is_otc: drug.is_otc,
                            med_cat: drug.med_cat,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            // Resetting these values to undefined
                            // clean up
                            doseCount: undefined,
                            medIndex: undefined,
                            tapIndex: undefined,
                            key: undefined,
                        },
                        drug.medIndex,
                    );
                    return;
                }
                upsertElement(
                    {
                        ...drug,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        // Resetting these values to undefined
                        // clean up
                        doseCount: undefined,
                        medIndex: undefined,
                        tapIndex: undefined,
                        key: undefined,
                    },
                    drug.medIndex,
                );
            });
        });
    };

    renderTestOnDate = (date: string, el: any, index: number) => {
        const { elements, upsertElement, containerId, onCopyTestOn } = this.props;

        return {
            children: (
                <>
                    <LabTestsDateSelector
                        defaultOptions={labTestOnDefaultOption}
                        date={date}
                        upsertElement={upsertElement}
                        elements={elements}
                        el={el}
                        containerId={containerId}
                        type={LAB_TESTS_FOLLOWUP_TYPE.TEST_ON}
                    />

                    {onCopyTestOn && (
                        <CopyToAll
                            id={`copy-test-on-date-v2-svg-${el?.id}`}
                            onClick={() => onCopyTestOn(el.id)}
                            tableId={'lx-reading-table'}
                        />
                    )}
                </>
            ),
        };
    };

    renderRepeatOnDate = (date: string, el: any, index: number) => {
        const { elements, upsertElement, containerId, onCopyRepeatOn } = this.props;

        return {
            children: (
                <>
                    <LabTestsDateSelector
                        defaultOptions={[]}
                        date={date}
                        upsertElement={upsertElement}
                        elements={elements}
                        el={el}
                        containerId={containerId}
                        type={LAB_TESTS_FOLLOWUP_TYPE.REPEAT_ON}
                    />
                    {onCopyRepeatOn && (
                        <CopyToAll
                            id={`copy-repeat-on-date-v2-svg-${el?.id}`}
                            onClick={() => onCopyRepeatOn(el.id)}
                            tableId={'lx-reading-table'}
                        />
                    )}
                </>
            ),
        };
    };

    toggleActiveLabTestElement = (element?: LabTestsEntityOrProceduresEntity): void => {
        this.setState({
            activeLabTestElement: element,
        });
        if (element?.id) {
            track('INCLUDED_TESTS_IN_PACKAGE_PANEL_DETAILS', { labTestId: element?.id });
        }
    };

    render(): ReactNode {
        const {
            kind,
            elements,
            elementsConfig,
            containerId,
            isNameEditable,
            searchComponent,
            onSortEnd,
            hideFixNowStrip,
        } = this.props;

        const {
            medInfoPopoverVisible,
            medGenericPopoverVisible,
            draggingIndex,
            isAddDrugDialogVisible,
            activeLabTestElement,
            doseCalculatorMed,
        } = this.state;

        const flatArray = getFlatArray(elements, kind, hideFixNowStrip);
        const uncodedDrugs = getUncodedDrugs(flatArray).filter((drug) => drug.tapIndex === -1);

        const medRowTop = isAddDrugDialogVisible
            ? document
                  .querySelector(`.med-table-${isAddDrugDialogVisible.medIndex}`)
                  ?.getBoundingClientRect().top
            : undefined;

        const medContainerTop = document
            .querySelector(`#${MED_TABLE_CONTAINER}`)
            ?.getBoundingClientRect().top;

        return (
            <div id={MED_TABLE_CONTAINER} className="relative">
                {isAddDrugDialogVisible && medRowTop && medContainerTop && (
                    <div
                        className="absolute"
                        style={{
                            left: 0,
                            right: 0,
                            top: medRowTop - medContainerTop,
                            bottom: 0,
                            zIndex: 100,
                        }}
                    >
                        <div className="w-full shadow-800 border border-darwin-neutral-200">
                            <AddDrugDialogWrapper
                                drug={isAddDrugDialogVisible.option}
                                onSelect={this.onAddDrug}
                                _source="INLINE"
                                onEdit={this.onEditDrug}
                            />
                        </div>
                    </div>
                )}
                {elements.length > 0 && (
                    <Table
                        id={this.TABLE_ID}
                        tableLayout="fixed"
                        className={kind === MEDICINES_KIND ? 'mx-table' : 'lx-reading-table'}
                        components={this.components}
                        rowClassName={(record, index) => `editable-row med-table-${index}`}
                        bordered
                        dataSource={flatArray}
                        pagination={false}
                        rowKey={(record, index) => `${this.props.kind}-${index}`}
                        scroll={
                            kind === MEDICINES_KIND
                                ? {
                                      x: 840,
                                  }
                                : undefined
                        }
                    >
                        <Column
                            fixed={true}
                            title={
                                kind === MEDICINES_KIND
                                    ? this.nameColumnheader
                                    : this.labResultNameColumnheader
                            }
                            onHeaderCell={headerStyle}
                            key="name"
                            onCell={(_, index) => {
                                if (index === undefined) {
                                    return {};
                                }
                                const colSpan =
                                    flatArray[index].id === 'add-drug'
                                        ? elementsConfig.filter((t) => t.isShown).length + 2
                                        : undefined;
                                return {
                                    rowSpan: !colSpan ? flatArray[index].doseCount || 0 : 1,
                                    colSpan: colSpan,
                                    className: flatArray[index].id === 'add-drug' ? '' : 'p-5',
                                    style:
                                        flatArray[index].id === 'add-drug'
                                            ? {
                                                  backgroundColor:
                                                      'rgba(255, 242, 241, var(--tw-bg-opacity))',
                                              }
                                            : undefined,
                                };
                            }}
                            className={`${
                                kind === MEDICINES_KIND ? 'mx-name' : 'lx-name'
                            } relative`}
                            render={(_, __, index) => {
                                const element = flatArray[index];

                                if (
                                    kind === MEDICINES_KIND &&
                                    element.id === 'add-drug' &&
                                    uncodedDrugs.length
                                ) {
                                    return {
                                        children: (
                                            <AddDrugStrip
                                                key={
                                                    'add-drug ' +
                                                    uncodedDrugs.map((t) => t.id).join('-')
                                                }
                                                drugs={uncodedDrugs}
                                                onSave={this.saveDrugs}
                                                focusDrugName={(elId) => this.focusDrugName(elId)}
                                            />
                                        ),
                                    };
                                }

                                if (!element.doseCount) {
                                    return null;
                                }

                                const elName = isNameEditable ? (
                                    <EditableCell
                                        key={flatArray[index].id + '-' + index}
                                        containerId={containerId}
                                        label={kind === MEDICINES_KIND ? 'Drug Name' : 'Lab test'}
                                        fieldName={`dn-${index}`}
                                        setRef={(ref) => {
                                            this.setPropertyInputRef(ref, index, 0);
                                        }}
                                        debounceDelay={200}
                                        customDropdownWidth={300}
                                        customDropdownClass={`py-8 ${
                                            kind === MEDICINES_KIND ? 'mx-dropdown' : 'lx-dropdown'
                                        } rounded-8`}
                                        onSearch={this.handleSearch}
                                        getLabelToCompare={(o) => o.name}
                                        optionsRenderer={this.renderCellAutosuggestItemJsx}
                                        selectedValue={
                                            flatArray[index] && flatArray[index].id
                                                ? {
                                                      id: flatArray[index].id,
                                                      name: flatArray[index].name,
                                                  }
                                                : undefined
                                        }
                                        onSelect={(option, searchString) =>
                                            this.onElementSelect(
                                                element.medIndex,
                                                index,
                                                option,
                                                searchString,
                                            )
                                        }
                                        placeholder={
                                            kind === MEDICINES_KIND ? 'Drug Name' : 'Lab test'
                                        }
                                    />
                                ) : (
                                    <span>{flatArray[index].name}</span>
                                );

                                const isNameEdited =
                                    element.actual_name && element.actual_name !== element.name;

                                const showIncludedTestsIcon =
                                    isOnboardedForLab() &&
                                    (element as LabTestsEntityOrProceduresEntity).book &&
                                    (isHXNGVendorPackageOrDoctorPackage(element) ||
                                        element.id.startsWith('lp-'));
                                const data = flatArray[index];
                                const inventory = data.inventory_status
                                    ? {
                                          mdb_id: data.id,
                                          quantity: data.inventory_status.quantity,
                                          mrp: data.inventory_status.price,
                                          sku: (data as Drug).product_sku || '',
                                          in_stock: data.inventory_status.in_stock,
                                          evitalId: data.inventory_status.evital_id,
                                      }
                                    : undefined;

                                return {
                                    children:
                                        kind === LAB_TESTS_KIND ? (
                                            <div className="space-y-4">
                                                <div className="font-700 flex space-x-8 items-center">
                                                    <DragHandle />
                                                    {elName}
                                                    <CustomEntityTooltip
                                                        id={flatArray[index].id}
                                                        kind="lab test"
                                                    />
                                                    <div className="flex pr-10">
                                                        {isOnboardedForLab() &&
                                                            this.bookLabTestsStatusIcon(
                                                                (element as LabTestsEntityOrProceduresEntity)
                                                                    .book
                                                                    ? 'inStock'
                                                                    : 'outOfStock',
                                                            )}
                                                    </div>
                                                </div>
                                                <div className="pl-34 flex items-center justify-between pr-12 cursor-pointer">
                                                    <div className="flex items-center space-x-8">
                                                        {isOnboardedForLab() &&
                                                            this.bookLabTestsStatusText(element)}
                                                        <TestingType
                                                            i={element}
                                                            className="px-4 py-2 mt-4 text-12"
                                                        />
                                                    </div>

                                                    {showIncludedTestsIcon && (
                                                        <AntTooltip
                                                            placement="bottom"
                                                            content={`Show tests included in ${element.name}`}
                                                            getPopupContainer={() =>
                                                                document.getElementById(
                                                                    element.id,
                                                                ) || document.body
                                                            }
                                                        >
                                                            <div
                                                                onClick={() =>
                                                                    this.toggleActiveLabTestElement(
                                                                        element,
                                                                    )
                                                                }
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="w-16 h-16 fill-darwin-primary-500"
                                                                    viewBox="0 0 12 13"
                                                                >
                                                                    <path d="M6 0.5C2.67188 0.5 0 3.19531 0 6.5C0 9.82812 2.67188 12.5 6 12.5C9.30469 12.5 12 9.82812 12 6.5C12 3.19531 9.30469 0.5 6 0.5ZM6 11.375C3.30469 11.375 1.125 9.19531 1.125 6.5C1.125 3.82812 3.30469 1.625 6 1.625C8.67188 1.625 10.875 3.82812 10.875 6.5C10.875 9.19531 8.67188 11.375 6 11.375ZM6.9375 8.375H6.5625V6.3125C6.5625 6.00781 6.30469 5.75 6 5.75H5.25C4.92188 5.75 4.6875 6.00781 4.6875 6.3125C4.6875 6.64062 4.92188 6.875 5.25 6.875H5.4375V8.375H5.0625C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z" />
                                                                </svg>
                                                            </div>
                                                        </AntTooltip>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full space-y-4 pb-10">
                                                <div className="flex items-baseline justify-between w-full">
                                                    <div className="flex items-center w-75p">
                                                        {onSortEnd && <DragHandle />}
                                                        <div className="flex font-700 wrap-full w-full text-neutral-black text-14 placeholder-neutral-6 ml-8">
                                                            {elName}{' '}
                                                        </div>
                                                    </div>

                                                    <CustomEntityTooltip id={flatArray[index].id} />
                                                    <div className="flex">
                                                        {kind === MEDICINES_KIND &&
                                                            isOnboardedForPharma() &&
                                                            this.substitutesStatusIcon(element)}
                                                    </div>
                                                    <div className="flex">
                                                        {kind === MEDICINES_KIND &&
                                                            !flatArray[index].id.startsWith(
                                                                LOCAL_ID_PREFIX,
                                                            ) &&
                                                            inventory &&
                                                            InventoryStatus(inventory)}
                                                    </div>
                                                    <div className="flex items-center space-x-4 ml-4">
                                                        {flatArray[index].id.startsWith(
                                                            LOCAL_ID_PREFIX,
                                                        ) &&
                                                            (element.generic_name ||
                                                                element.dosage_form ||
                                                                element.form) && (
                                                                <EditMedInfoCTA
                                                                    visible={
                                                                        medInfoPopoverVisible ===
                                                                        index
                                                                    }
                                                                    element={element}
                                                                    onVisibleChange={(v) =>
                                                                        this.setState({
                                                                            medInfoPopoverVisible: v
                                                                                ? index
                                                                                : undefined,
                                                                        })
                                                                    }
                                                                    saveMedInfo={(form, generic) =>
                                                                        this.saveMedInfo(
                                                                            element.medIndex,
                                                                            form,
                                                                            generic,
                                                                        )
                                                                    }
                                                                    containerId={containerId}
                                                                >
                                                                    <span>
                                                                        <svg
                                                                            className="w-12 h-12 cursor-pointer fill-actionPrimary"
                                                                            role="img"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 512 512"
                                                                        >
                                                                            <path d="M495.6 49.23l-32.82-32.82C451.8 5.471 437.5 0 423.1 0c-14.33 0-28.66 5.469-39.6 16.41L167.5 232.5C159.1 240 154.8 249.5 152.4 259.8L128.3 367.2C126.5 376.1 133.4 384 141.1 384c.916 0 1.852-.0918 2.797-.2813c0 0 74.03-15.71 107.4-23.56c10.1-2.377 19.13-7.459 26.46-14.79l217-217C517.5 106.5 517.4 71.1 495.6 49.23zM461.7 94.4L244.7 311.4C243.6 312.5 242.5 313.1 241.2 313.4c-13.7 3.227-34.65 7.857-54.3 12.14l12.41-55.2C199.6 268.9 200.3 267.5 201.4 266.5l216.1-216.1C419.4 48.41 421.6 48 423.1 48s3.715 .4062 5.65 2.342l32.82 32.83C464.8 86.34 464.8 91.27 461.7 94.4zM424 288c-13.25 0-24 10.75-24 24v128c0 13.23-10.78 24-24 24h-304c-13.22 0-24-10.77-24-24v-304c0-13.23 10.78-24 24-24h144c13.25 0 24-10.75 24-24S229.3 64 216 64L71.1 63.99C32.31 63.99 0 96.29 0 135.1v304C0 479.7 32.31 512 71.1 512h303.1c39.69 0 71.1-32.3 71.1-72L448 312C448 298.8 437.3 288 424 288z"></path>
                                                                        </svg>
                                                                    </span>
                                                                </EditMedInfoCTA>
                                                            )}

                                                        {(element.dosage_form || element.form) && (
                                                            <span className=" text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-10">
                                                                {element.dosage_form ||
                                                                    element.form}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="pl-36">
                                                    <p className="text-neutral-4 text-10 uppercase truncate-overflow-2">
                                                        {flatArray[index] &&
                                                        flatArray[index].generic_name ? (
                                                            flatArray[index].generic_name
                                                        ) : flatArray[index].id.startsWith(
                                                              LOCAL_ID_PREFIX,
                                                          ) &&
                                                          !(element.form || element.dosage_form) ? (
                                                            <EditMedInfoCTA
                                                                visible={
                                                                    medGenericPopoverVisible ===
                                                                    index
                                                                }
                                                                element={element}
                                                                onVisibleChange={(v) =>
                                                                    this.setState({
                                                                        medGenericPopoverVisible: v
                                                                            ? index
                                                                            : undefined,
                                                                    })
                                                                }
                                                                saveMedInfo={(form, generic) =>
                                                                    this.saveMedInfo(
                                                                        element.medIndex,
                                                                        form,
                                                                        generic,
                                                                    )
                                                                }
                                                                containerId={this.props.containerId}
                                                            >
                                                                <span className="text-actionPrimary cursor-pointer">
                                                                    + Add Generic and Form here
                                                                </span>
                                                            </EditMedInfoCTA>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </p>
                                                    <div>
                                                        {kind === MEDICINES_KIND &&
                                                            isOnboardedForPharma() &&
                                                            this.orderMedicinesStatusText(element)}
                                                        {kind === MEDICINES_KIND &&
                                                            inventory?.in_stock === false && (
                                                                <div
                                                                    className={` px-4 py-2 mt-4 rounded-4 ${medicinesInventoryStatusOptions.outOfStock.bg}`}
                                                                    style={{ width: 'fit-content' }}
                                                                >
                                                                    <p
                                                                        className={`font-700 text-12 ${medicinesInventoryStatusOptions.outOfStock.textColor}`}
                                                                    >
                                                                        {
                                                                            medicinesInventoryStatusOptions
                                                                                .outOfStock.text
                                                                        }
                                                                    </p>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>

                                                <RenameMedName
                                                    originalName={
                                                        element.actual_name || element.name
                                                    }
                                                    defaultName={element.name}
                                                    onSave={(newName: string) =>
                                                        this.onRename(element.medIndex, newName)
                                                    }
                                                    isNameEdited={isNameEdited || false}
                                                    onReset={() => this.onReset(element.medIndex)}
                                                />
                                                <div
                                                    onClick={() => this.onAddDose(element.medIndex)}
                                                    className="absolute bottom-0 right-0"
                                                >
                                                    <div className="w-20 h-20 bg-neutral-9 min-w-20 min-h-20 flex items-center justify-center cursor-pointer tapering-shortform">
                                                        <svg
                                                            className="w-14 h-14 fill-neutral-5"
                                                            role="img"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 448 512"
                                                        >
                                                            <path d="M432 256C432 269.3 421.3 280 408 280h-160v160c0 13.25-10.75 24.01-24 24.01S200 453.3 200 440v-160h-160c-13.25 0-24-10.74-24-23.99C16 242.8 26.75 232 40 232h160v-160c0-13.25 10.75-23.99 24-23.99S248 58.75 248 72v160h160C421.3 232 432 242.8 432 256z"></path>
                                                        </svg>
                                                    </div>
                                                    <div className="space-x-4 flex items-center bg-neutral-8 px-8 tapering-longform cursor-pointer">
                                                        <svg
                                                            className="w-14 h-14 fill-actionPrimary"
                                                            role="img"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 448 512"
                                                        >
                                                            <path d="M432 256C432 269.3 421.3 280 408 280h-160v160c0 13.25-10.75 24.01-24 24.01S200 453.3 200 440v-160h-160c-13.25 0-24-10.74-24-23.99C16 242.8 26.75 232 40 232h160v-160c0-13.25 10.75-23.99 24-23.99S248 58.75 248 72v160h160C421.3 232 432 242.8 432 256z"></path>
                                                        </svg>
                                                        <p className="text-actionPrimary font-700 text-12">
                                                            Tapering Dose
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                };
                            }}
                        />
                        {kind === MEDICINES_KIND ? (
                            <Fragment>
                                {elementsConfig
                                    .filter((t) => t.isShown)
                                    .map((t, columnIndex) => {
                                        switch (t.id) {
                                            case MED_TABLE_COL_DOSE_KEY:
                                                return this.getDoseColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_FREQUENCY_KEY:
                                                return this.getFrequencyColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_TIMING_KEY:
                                                return this.getTimingColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_DURATION_KEY:
                                                return this.getDurationColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_START_FROM_KEY:
                                                return this.getStartFromColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_INSTRUCTION_KEY:
                                                return this.getMedInstructionColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            case MED_TABLE_COL_QUANTITY_KEY:
                                                return this.getQuantityColumn(
                                                    flatArray,
                                                    columnIndex + 1,
                                                );
                                            default:
                                                return null;
                                        }
                                    })}
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Column
                                    title={this.dateTestContent}
                                    onHeaderCell={headerStyle}
                                    key="lab-vital-row-date"
                                    className="lab-vital-row-date"
                                    dataIndex={'test_on'}
                                    width={120}
                                    render={this.renderTestOnDate}
                                />
                                <Column
                                    title={this.dateRepeatContent}
                                    onHeaderCell={headerStyle}
                                    key="lab-vital-row-date"
                                    className="lab-vital-row-date"
                                    dataIndex={'repeat_on'}
                                    width={120}
                                    render={this.renderRepeatOnDate}
                                />
                                <Column
                                    title={this.remarksColumns}
                                    className={`p-5 ${'lx-instructions'}`}
                                    onHeaderCell={headerStyle}
                                    width={230}
                                    key="instruction"
                                    render={(_, el: FlatMedObj, index) => {
                                        if (draggingIndex !== undefined && el.tapIndex > -1)
                                            return null;

                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        const value = el.remark || '';

                                        return {
                                            children: (
                                                <div className="flex space-x-8 items-center">
                                                    <EditableCellV2
                                                        defaultValue={value}
                                                        onBlur={(e) => {
                                                            this.onInstructionBlur(
                                                                el.medIndex,
                                                                el.tapIndex,
                                                                e.target.value,
                                                            );
                                                        }}
                                                        setRef={(ref) => {
                                                            this.setPropertyInputRef(ref, index, 6);
                                                            return;
                                                        }}
                                                        label="Instructions"
                                                    >
                                                        <Input.TextArea
                                                            autoFocus
                                                            defaultValue={value}
                                                            rows={2}
                                                            placeholder="Add notes here"
                                                            onBlur={(e) => {
                                                                this.onInstructionBlur(
                                                                    el.medIndex,
                                                                    el.tapIndex,
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        />
                                                    </EditableCellV2>
                                                </div>
                                            ),
                                        };
                                    }}
                                />
                            </Fragment>
                        )}

                        <Column
                            className={`p-5 delete-button`}
                            fixed="right"
                            key="delete"
                            onHeaderCell={headerStyle}
                            onCell={(record: FlatMedObj, rowIndex?: number) => {
                                if (rowIndex == undefined) {
                                    return {};
                                }
                                if (record.id === 'add-drug') {
                                    return {
                                        className: 'hidden',
                                    };
                                }
                                return {};
                            }}
                            render={(_, el: FlatMedObj) => {
                                const { onDeleteElement } = this.props;

                                if (!onDeleteElement && el.tapIndex === -1) {
                                    return null;
                                }

                                if (el.id === 'add-drug') {
                                    return null;
                                }

                                if (draggingIndex !== undefined && el.tapIndex > -1) return null;

                                return {
                                    children: (
                                        <div className="flex space-x-8 items-center justify-center">
                                            <DeleteOutlined
                                                style={{ color: '#616A8F' }}
                                                size={24}
                                                onClick={() =>
                                                    this.onDeleteRow(el.medIndex, el.tapIndex)
                                                }
                                                onPointerLeaveCapture={undefined}
                                            />
                                        </div>
                                    ),
                                };
                            }}
                        />
                    </Table>
                )}
                {searchComponent}

                <LabTestsIncludedInPackagePanel
                    activeLabTestElement={activeLabTestElement}
                    closeMe={() => this.toggleActiveLabTestElement()}
                />

                {doseCalculatorMed ? (
                    <CalculatorRightPopUp
                        calculatorName={CALCULATORS_NAMES.PEDIATRIC_DOSE}
                        closeMe={() => this.setState({ doseCalculatorMed: undefined })}
                        onElementUpdate={(option, _, itemIdx, valueObject, unitObject) => {
                            if (itemIdx === 0) {
                                const { id, value } = option;
                                const { qt, unit } = value;
                                const updatedMed = {
                                    ...doseCalculatorMed,
                                    dose: {
                                        custom: `${qt} ${unit}`,
                                        id: id,
                                        value: qt,
                                        unit: unit,
                                    },
                                };
                                const newF = FREQUENCY_TO_TEXT_VALUE[valueObject?.frequency || 0];
                                if (newF) {
                                    updatedMed.frequency = {
                                        type: 'once every',
                                        custom: newF,
                                    };
                                }

                                if (valueObject) {
                                    if (!updatedMed.meta_info) updatedMed.meta_info = {};
                                    const entries: [
                                        keyof typeof valueObject,
                                        keyof NonNullable<typeof updatedMed.meta_info>,
                                    ][] = [
                                        ['dose', 'dose'],
                                        ['mg', 'mass'],
                                        ['volume', 'volume'],
                                    ];
                                    for (const [key, metaKey] of entries) {
                                        const val = valueObject[key];
                                        const unit = unitObject?.[key] ?? '';
                                        if (val != null) {
                                            (updatedMed.meta_info as Record<
                                                string,
                                                QuantityWithUnit
                                            >)[metaKey] = {
                                                value: val,
                                                unit: unit,
                                            };
                                        }
                                    }
                                }
                                this.props.upsertElement(updatedMed);
                            }
                        }}
                        propsDefaultValuesForCalculator={getPropsDefaultValuesForCalculator(
                            doseCalculatorMed,
                        )}
                        propsDefaultUnitsForCalculator={getPropsDefaultUnitsForCalculator(
                            doseCalculatorMed,
                        )}
                    />
                ) : null}
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    selectedClinic: getRxClinicId(state),
    drugsSubstitute: getDrugsSubstitute(state),
    isDoseCalculatorAvailable: getGenieSetting(state, 'isDoseCalculatorAvailable') as boolean,
    medRouteEnabled: getGenieSetting(state, 'medRouteEnabled') as boolean,
}))(MedTableRenderer);

type EP = {
    visible: boolean;
    element: FlatMedObj;
    onVisibleChange: (v: boolean) => void;
    saveMedInfo: (form: string, generic: string) => void;
    containerId?: string;
};

const EditMedInfoCTA: React.FC<EP> = ({
    element,
    children,
    onVisibleChange,
    saveMedInfo,
    visible,
    containerId,
}) => {
    return (
        <Popover
            placement="right"
            title={'Add details'}
            onVisibleChange={onVisibleChange}
            visible={visible}
            getPopupContainer={() => document.getElementById(PAD_V2_CONTAINER) || document.body}
            content={
                <MedAddDetails
                    form={element.dosage_form || element.form || ''}
                    generic={element.generic_name || ''}
                    onSave={saveMedInfo}
                    containerId={containerId}
                />
            }
            trigger="click"
        >
            {children}
        </Popover>
    );
};

type MIP = {
    form: string;
    generic: string;
    onSave: (form: string, generic: string) => void;
    containerId?: string;
};

const MedAddDetails: React.FC<MIP> = ({ form, generic, onSave, containerId }) => {
    const [lForm, setForm] = useState<string | undefined>(form);
    const [lGeneric, setGeneric] = useState(generic);

    const save = () => {
        onSave(lForm || '', lGeneric);
    };

    const onGenericPaste = (): void => {
        track('PASTE', { on: 'MED_TABLE_GENERIC' });
    };

    return (
        <div className="flex flex-col space-y-8">
            <div>
                <p className="font-700 text-12 text-neutral-3">Form</p>
            </div>
            <EditableCell
                containerId={containerId}
                autoFocus={false}
                withoutDiv
                withoutError
                label="Form"
                fieldName={`mform`}
                setRef={() => {
                    return;
                }}
                debounceDelay={0}
                onSearch={onFormSearch}
                selectedValue={lForm}
                getLabelToCompare={(o) => o}
                placeholder={'Dose'}
                onSelect={setForm}
            />

            <div>
                <p className="font-700 text-12 text-neutral-3">Generic</p>
            </div>
            <Input
                value={lGeneric}
                onChange={(e) => setGeneric(e.target.value)}
                placeholder="Generic"
                onPaste={onGenericPaste}
            />
            <div className="flex justify-end mt-8">
                <Button onClick={save} type="primary">
                    Save
                </Button>
            </div>
        </div>
    );
};

type AnyObject = { [k: string]: string | number | AnyObject };

type RenderProps = undefined | AnyObject | ((originProps: AnyObject) => AnyObject | undefined);

export function replaceElement(
    element: React.ReactNode,
    replacement: React.ReactNode,
    props: RenderProps,
): React.ReactNode {
    if (!React.isValidElement(element)) return replacement;

    return React.cloneElement(
        element,
        typeof props === 'function' ? props(element.props || {}) : props,
    );
}

export function cloneElement(element: React.ReactNode, props?: RenderProps): React.ReactElement {
    return replaceElement(element, element, props) as React.ReactElement;
}

const DoseCalculator = ({ onClick }: { onClick: () => void }) => {
    return (
        <div className="p-4 absolute bottom-0 right-0 cursor-pointer" onClick={onClick}>
            <CalculatorRegularIcon className="w-10 h-14 text-darwin-neutral-600" />
        </div>
    );
};

function getPropsDefaultValuesForCalculator(medication: FlatMedObj) {
    const { dose, mass, volume } = medication.meta_info || {};
    return {
        frequency: {
            value: medication.frequency?.custom,
        },
        dose: {
            value: dose?.value?.toString(),
        },
        mg: {
            value: mass?.value?.toString(),
        },
        volume: {
            value: volume?.value?.toString(),
        },
    };
}

function getPropsDefaultUnitsForCalculator(medication: FlatMedObj) {
    const { dose, mass, volume } = medication.meta_info || {};
    return {
        dose: dose?.unit || '',
        mg: mass?.unit || '',
        volume: volume?.unit || '',
    };
}
